import React, { useCallback, useContext, useMemo, useRef } from 'react';
import cn from 'classnames';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Scrollbar from 'react-scrollbars-custom';
import TableHead from './TableHead';
import TableFooter from './TableFooter';
import TableRow from './TableRow';
import { DEFAULT_SCROLLBAR_SIZE } from "@constants/general";
import { getScaledPixels as getScaledPixelsRaw, useWindowSizeDebounced } from "@datassential/platform-ui-lib";

interface OuterElementContextValue {
    headHeight: number;
    stickyRowsCount: number;
    rowHeight: number;
    contentWidthPx?: number;
    isLoading?: boolean;
    footerHeight: number;
    tableChildren?: React.ReactNode;
}

const OuterElementContext = React.createContext<OuterElementContextValue>({
    headHeight: 38,
    stickyRowsCount: 0,
    rowHeight: 38,
    footerHeight: 0,
});

const OuterElement = React.forwardRef<HTMLDivElement, any>(
    ({ children, ...props }, ref) => {
        const {
            headHeight,
            stickyRowsCount,
            rowHeight,
            contentWidthPx,
            isLoading,
            footerHeight,
            tableChildren,
        } = useContext(OuterElementContext);
        const scaledStickyRowHeight = rowHeight;

        return (
            <div {...props} ref={ref}>
                <div style={{ position: 'sticky', top: 0, zIndex: 100, height: headHeight }}>
                    <TableHead
                        height={headHeight}
                        style={{
                            minWidth: contentWidthPx ? `${contentWidthPx}px` : undefined,
                        }}
                    />
                </div>
                {stickyRowsCount > 0 && (
                    new Array(stickyRowsCount).fill(0).map((_, index) => (
                        <div
                            key={`stickyRow${index}`}
                            style={{
                                position: 'sticky',
                                top: headHeight + (index * scaledStickyRowHeight),
                                zIndex: 1,
                                height: scaledStickyRowHeight
                            }}
                        >
                            <TableRow
                                isSticky
                                index={index}
                                isLoading={isLoading}
                                style={{
                                    minWidth: contentWidthPx ? `${contentWidthPx}px` : undefined,
                                }}
                            />
                        </div>
                    ))
                )}
                {children}
                {footerHeight > 0 && (
                    <div style={{ position: 'sticky', bottom: 0, zIndex: 100, height: footerHeight }}>
                        <TableFooter
                            height={footerHeight}
                            style={{
                                minWidth: contentWidthPx ? `${contentWidthPx}px` : undefined,
                            }}
                        />
                    </div>
                )}
                {tableChildren}
            </div>
        );
    }
);

interface VirtualizedTableProps {
    isLoading?: boolean;
    headHeight?: number;
    rowHeight?: number;
    footerHeight?: number;
    rowsCount: number;
    stickyRowsCount?: number;
    contentWidthPx?: number;
    useContentHeight?: boolean;
    onRowHover?: (index: number | null) => void;
    showFallback?: boolean;
    style?: React.CSSProperties;
    children?: React.ReactNode;
    testId?: string;
    className?: string;
    scrollXTrackLeftPx?: number;
}

const VirtualizedTable: React.FC<VirtualizedTableProps> = ({
    isLoading,
    headHeight = 38,
    rowHeight = 38,
    footerHeight = 0,
    rowsCount,
    stickyRowsCount = 0,
    style,
    children: tableChildren,
    testId,
    className,
    contentWidthPx,
    onRowHover,
    useContentHeight = false,
    showFallback,
    scrollXTrackLeftPx = 0,
}) => {
    const listRef = useRef<FixedSizeList | null>(null);

    const [wWidth] = useWindowSizeDebounced();

    const getScaledPixels = useCallback(
        (px: number) => {
            return getScaledPixelsRaw(px, wWidth);
        },
        [wWidth]
    );

    const contentHeight = useMemo(() => {
        const totalRowsHeight = rowsCount * rowHeight;
        return headHeight + totalRowsHeight + footerHeight;
    }, [headHeight, rowHeight, rowsCount, footerHeight]);

    const defaultFallback = useMemo(
        () => {
            if (rowsCount === 0 && !isLoading && showFallback) {
                return (
                    <span className="Table__fallback">No matching items found</span>
                );
            }

            return null;
        },
        [rowsCount, isLoading, showFallback]
    );

    const outerElementContextValue = useMemo<OuterElementContextValue>(() => ({
        headHeight,
        stickyRowsCount,
        rowHeight,
        contentWidthPx,
        isLoading,
        footerHeight,
        tableChildren,
    }), [headHeight, stickyRowsCount, rowHeight, contentWidthPx, isLoading, footerHeight, tableChildren]);

    return (
        <div
            className={cn('Table', className)}
            style={{
                height: '100%',
                ...style,
            }}
            data-testid={testId}
        >
            <div style={{
                flex: 1,
                minHeight: 0,
                position: 'relative',
                maxHeight: useContentHeight ? contentHeight : undefined,
            }}>
                <OuterElementContext.Provider value={outerElementContextValue}>
                    <AutoSizer>
                        {({ width, height }: { width: number; height: number }) => {
                            return (
                                <div style={{ 
                                    position: 'relative', 
                                    width: width + getScaledPixels(DEFAULT_SCROLLBAR_SIZE),
                                    height: height + getScaledPixels(DEFAULT_SCROLLBAR_SIZE),
                                }}>
                                    <Scrollbar
                                        noScrollX={false}
                                        onScroll={
                                            // @ts-ignore
                                            ({ scrollTop }) => listRef.current?.scrollTo(scrollTop)
                                        }
                                        trackXProps={{
                                            renderer: ({ elementRef, style: trackStyle, ...restProps }) => (
                                                <div
                                                    {...restProps}
                                                    ref={elementRef}
                                                    style={{
                                                        ...trackStyle,
                                                        left: getScaledPixels(scrollXTrackLeftPx),
                                                        width: `calc(100% - ${getScaledPixels(scrollXTrackLeftPx)}px - ${getScaledPixels(DEFAULT_SCROLLBAR_SIZE)}px)`,
                                                    }}
                                                />
                                            ),
                                        }}
                                    >
                                        <div style={{
                                            minHeight: height,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}>
                                            <FixedSizeList
                                                ref={listRef}
                                                itemSize={rowHeight}
                                                width={width}
                                                height={height}
                                                itemCount={rowsCount - stickyRowsCount}
                                                outerElementType={OuterElement}
                                                className="window-scroller-override"
                                            >
                                                {({ index, style: itemStyle }) => (
                                                    <TableRow
                                                        index={index + stickyRowsCount}
                                                        isLoading={isLoading}
                                                        onHover={onRowHover}
                                                        style={{
                                                            ...itemStyle,
                                                            top: (itemStyle.top as number) + headHeight + (stickyRowsCount * rowHeight),
                                                            minWidth: contentWidthPx ? `${contentWidthPx}px` : undefined,
                                                        }}
                                                    />
                                                )}
                                            </FixedSizeList>
                                            {defaultFallback}
                                        </div>
                                    </Scrollbar>
                                </div>
                            );
                        }}
                    </AutoSizer>
                </OuterElementContext.Provider>
            </div>
        </div>
    );
};

export default VirtualizedTable;
